import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Layout from './components/Layout/Layout';
import LoadingSpinner from './components/UI/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';

// Auth Pages
import LoginPage from './pages/Auth/LoginPage'; 
// import RegisterPage from './pages/Auth/RegisterPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import VerifyEmailPage from './pages/Auth/VerifyEmailPage';

// Main Pages
import DashboardPage from './pages/Dashboard/DashboardPage';
import CampaignsPage from './pages/Campaigns/CampaignsPage';
import CampaignDetailsPage from './pages/Campaigns/CampaignDetailsPage';
import CreateCampaignPage from './pages/Campaigns/CreateCampaignPage';
import EditCampaignPage from './pages/Campaigns/EditCampaignPage';
import LeadsPage from './pages/Leads/LeadsPage';
import AddLeadPage from './pages/Leads/AddLeadPage';
import EditLeadPage from './pages/Leads/EditLeadPage';
import ViewLeadPage from './pages/Leads/ViewLeadPage';
import ImportLeadsPage from './pages/Leads/ImportLeadsPage';
import ResultsPage from './pages/Results/ResultsPage';
import GlobalResultsPage from './pages/Global/GlobalResultsPage';
import CallsPage from './pages/Calls/CallsPage';
import SettingsPage from './pages/Settings/SettingsPage';
import AgentDashboardPage from './pages/Agent/AgentDashboardPage';
import AgentCallHistoryPage from './pages/Agent/AgentCallHistoryPage';
import AgentResultsPage from './pages/Agent/AgentResultsPage';
import AgentManualDialPage from './pages/Agent/AgentManualDialPage';

import AdminPage from './pages/Admin/AdminPage';
import LiveCallsPage from './pages/LiveCalls/LiveCallsPage';
import LiveMonitorPage from './pages/Admin/LiveMonitorPage';
import AutoDialMonitorPage from './pages/Admin/AutoDialMonitorPage';
import ConnectReportPage from './pages/Admin/ConnectReportPage';
import SmsLiveMonitorPage from './pages/Admin/SmsLiveMonitorPage';
import BotMonitorPage from './pages/Admin/BotMonitorPage';
import AdminSubStageConfigPage from './pages/Admin/AdminSubStageConfigPage';
import AdminSmsTemplatesPage from './pages/Admin/AdminSmsTemplatesPage';
import AdminPipelinePage from './pages/Admin/AdminPipelinePage';
import ViewChargesPage from './pages/Admin/ViewChargesPage';
import ScriptsPage from './pages/Admin/ScriptsPage';

import WebhookDashboardPage from './pages/Webhooks/WebhookDashboardPage';
import ProfilePage from './pages/Profile/ProfilePage';
import PhoneNumbersPage from './pages/PhoneNumbers/PhoneNumbersPage';
import SmsInboxPage from './pages/Sms/SmsInboxPage';
import SmsConversationPage from './pages/Sms/SmsConversationPage';
import DuplicateLeadsPage from './pages/DuplicateLeads/DuplicateLeadsPage';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const hasTokens =
    Boolean(localStorage.getItem('accessToken')) ||
    Boolean(localStorage.getItem('refreshToken'));

  if (loading && !hasTokens) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirect if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (user) {
    return <Navigate to={user.role === 'agent' ? '/agent-dashboard' : '/dashboard'} replace />;
  }

  return <>{children}</>;
};

// Agent Route Component
const AgentRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'agent') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Admin Route Component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Admin + Owner (matches phone-numbers API authorize)
const AdminOwnerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'admin' && user.role !== 'owner') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Bot Monitor: admin/owner (all campaigns) or agent (API scopes to assigned bot campaigns)
const BotMonitorRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'admin' && user.role !== 'owner' && user.role !== 'agent') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <ThemeProvider>
      <ErrorBoundary>
      <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          {/* <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterPage />
              </PublicRoute>
            }
          /> */}
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            }
          />
          <Route
            path="/verify-email"
            element={
              <PublicRoute>
                <VerifyEmailPage />
              </PublicRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route
              path="/agent-dashboard"
              element={
                <AgentRoute>
                  <AgentDashboardPage />
                </AgentRoute>
              }
            />
            <Route
              path="/agent-calls"
              element={
                <AgentRoute>
                  <AgentCallHistoryPage />
                </AgentRoute>
              }
            />
            <Route
              path="/agent-results"
              element={
                <AgentRoute>
                  <AgentResultsPage />
                </AgentRoute>
              }
            />
            <Route
              path="/agent-manual-dial"
              element={
                <AgentRoute>
                  <AgentManualDialPage />
                </AgentRoute>
              }
            />
            <Route path="/campaigns" element={<CampaignsPage />} />
            <Route path="/campaigns/create" element={<CreateCampaignPage />} />
            <Route path="/campaigns/:id" element={<CampaignDetailsPage />} />
            <Route path="/campaigns/:id/edit" element={<EditCampaignPage />} />
            <Route path="/campaigns/:id/leads" element={<LeadsPage />} />
            <Route path="/campaigns/:id/leads/new" element={<AddLeadPage />} />
            <Route path="/campaigns/:id/leads/import" element={<ImportLeadsPage />} />
            <Route path="/campaigns/:campaignId/leads/:leadId" element={<ViewLeadPage />} />
            <Route path="/campaigns/:campaignId/leads/:leadId/edit" element={<EditLeadPage />} />
            <Route path="/campaigns/:id/results" element={<ResultsPage />} />
            <Route path="/campaigns/:id/calls" element={<CallsPage />} />
            <Route path="/sms/inbox" element={<SmsInboxPage />} />
            <Route path="/sms/conversations/:id" element={<SmsConversationPage />} />
            
            {/* Global Routes */}
            <Route path="/results" element={<GlobalResultsPage />} />
            <Route path="/duplicate-leads" element={<DuplicateLeadsPage />} />
            <Route path="/calls" element={<CallsPage />} />

            <Route
              path="/settings"
              element={
                <AdminRoute>
                  <SettingsPage />
                </AdminRoute>
              }
            />
            <Route path="/profile" element={<ProfilePage />} />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/area-codes"
              element={
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/sub-stages"
              element={
                <AdminRoute>
                  <AdminSubStageConfigPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/sms-templates"
              element={
                <AdminRoute>
                  <AdminSmsTemplatesPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/pipeline"
              element={
                <AdminRoute>
                  <AdminPipelinePage />
                </AdminRoute>
              }
            />
            <Route
              path="/scripts"
              element={
                <AdminRoute>
                  <ScriptsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/scripts/new"
              element={
                <AdminRoute>
                  <ScriptsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/scripts/:id/edit"
              element={
                <AdminRoute>
                  <ScriptsPage />
                </AdminRoute>
              }
            />

            <Route
              path="/charges"
              element={
                <AdminRoute>
                  <ViewChargesPage />
                </AdminRoute>
              }
            />

            <Route
              path="/live-calls"
              element={
                <AdminRoute>
                  <LiveCallsPage />
                </AdminRoute>
              }
            />

            <Route
              path="/live-monitor"
              element={
                <AdminRoute>
                  <LiveMonitorPage />
                </AdminRoute>
              }
            />

            <Route
              path="/auto-dial-monitor"
              element={
                <AdminRoute>
                  <AutoDialMonitorPage />
                </AdminRoute>
              }
            />

            <Route
              path="/connect-report"
              element={
                <AdminRoute>
                  <ConnectReportPage />
                </AdminRoute>
              }
            />

            <Route
              path="/sms-live-monitor"
              element={
                <AdminRoute>
                  <SmsLiveMonitorPage />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/bot-monitor"
              element={
                <BotMonitorRoute>
                  <BotMonitorPage />
                </BotMonitorRoute>
              }
            />

            <Route
              path="/phone-numbers"
              element={
                <AdminOwnerRoute>
                  <PhoneNumbersPage />
                </AdminOwnerRoute>
              }
            />

            {/* Legacy routes redirect */}
            <Route
              path="/phone-numbers/:category"
              element={
                <AdminOwnerRoute>
                  <PhoneNumbersPage />
                </AdminOwnerRoute>
              }
            />

            <Route path="/webhooks" element={<WebhookDashboardPage />} />

            {/* 404 Route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
      </Routes>
      </div>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;